import React from 'react'
import {Button, DatePicker} from 'antd'
import {Dayjs} from 'dayjs'
import {useIntl} from 'react-intl'
import {useIsMobileNarrow} from '../../../hooks/useMediaQuery'
import './dateRangePicker.less'

export type DateRangeValue = [Dayjs | null, Dayjs | null] | null

export type DateRangePreset = {
  label: string
  value: [Dayjs, Dayjs]
}

export type DateRangePickerProps = {
  value?: DateRangeValue
  onChange?: (value: DateRangeValue) => void
  id?: string
  disabled?: boolean
  presets?: DateRangePreset[]
}

// Antd's range picker opens a two-month panel that is wider than a narrow
// screen, so below the mobile-narrow breakpoint the same value is edited with
// a pair of plain single-date pickers instead.
export function DateRangePicker({value, onChange, id, disabled, presets}: DateRangePickerProps) {
  const intl = useIntl()
  const isMobileNarrow = useIsMobileNarrow()

  const [start, end] = value || [null, null]

  if (!isMobileNarrow) {
    return (
      <DatePicker.RangePicker
        id={id}
        disabled={disabled}
        value={value}
        presets={presets}
        onChange={(dates) => onChange?.((dates as DateRangeValue) || [null, null])}
      />
    )
  }

  return (
    <div className="date-range-picker">
      {/* The wide panel shows presets in its own side column; without that panel
          they need a row of their own. */}
      {presets?.length > 0 && (
        <div className="date-range-picker__presets">
          {presets.map((preset) => (
            <Button
              key={preset.label}
              size="small"
              disabled={disabled}
              onClick={() => onChange?.(preset.value)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      )}
      <div className="date-range-picker__fields">
        <DatePicker
          id={id}
          disabled={disabled}
          value={start}
          maxDate={end || undefined}
          placeholder={intl.formatMessage({id: 'startDate'})}
          onChange={(newStart) => onChange?.([newStart, end])}
        />
        <DatePicker
          disabled={disabled}
          value={end}
          minDate={start || undefined}
          placeholder={intl.formatMessage({id: 'endDate'})}
          onChange={(newEnd) => onChange?.([start, newEnd])}
        />
      </div>
    </div>
  )
}
